<template>
  <div style="display: inline-block">
    <!-- Simple Button -->
    <b-button
      variant="danger"
      :class="buttonClass"
      :disabled="disabled"
      size="sm"
      pill
      @click="showReasonModal"
    >
      영구정지
    </b-button>

    <!-- Reason Selection Modal -->
    <b-modal
      v-model="reasonModalShow"
      hide-footer
      hide-header
      style="text-align: center"
    >
      <h5 style="text-align: center">
        정지 사유를 선택해주세요
      </h5>
      <b-form-group>
        <b-form-checkbox-group
          size="sm"
          v-model="selectedReasonArray"
          :options="banReasons"
          button-variant="outline-primary"
          stacked
          buttons
          style="
            width: 100%;
            word-break: keep-all;
            word-wrap: normal;
          "
        ></b-form-checkbox-group>
      </b-form-group>
      <div class="d-block text-center lg">
        <b-button
          variant="success"
          pill
          @click="proceedToConfirmation"
          :disabled="selectedReasonArray.length === 0"
          style="margin-right: 15px"
        >
          확인
        </b-button>
        <b-button
          variant="danger"
          pill
          @click="cancelReasonSelection"
          style="margin-left: 15px"
        >
          취소
        </b-button>
      </div>
    </b-modal>

    <!-- Confirmation Modal -->
    <b-modal
      v-model="confirmModalShow"
      hide-footer
      hide-header
      style="text-align: center"
    >
      <h5 style="text-align: center">
        해당 유저를 영구정지 하시겠습니까?
      </h5>
      <p style="margin-top: 10px; color: #dc3545; font-size: 14px;">
        정지 사유: {{ selectedReason ? selectedReason.text : '' }}
      </p>
      <p style="color: #dc3545; font-size: 14px;">
        영구정지된 유저는 모든 서비스에서 차단됩니다.
      </p>
      <div class="d-block text-center lg">
        <b-button
          variant="success"
          pill
          @click="executeBan"
          :disabled="isExecuting"
          style="margin-right: 15px"
        >
          <b-spinner v-if="isExecuting" small></b-spinner>
          {{ isExecuting ? '처리중...' : '확인' }}
        </b-button>
        <b-button
          variant="danger"
          pill
          @click="cancelBan"
          :disabled="isExecuting"
          style="margin-left: 15px"
        >
          취소
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { reports } from "../apis/index.js";
import { getDomainLabel } from "./ReportCheck/reasonLabel";

export default {
  name: "PermanentBanButton",
  props: {
    userId: {
      type: [String, Number],
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    buttonClass: {
      type: String,
      default: "resultBtn"
    }
  },
  data() {
    return {
      banReasons: [],
      selectedReason: null,
      selectedReasonArray: [], // For checkbox group
      reasonModalShow: false,
      confirmModalShow: false,
      isExecuting: false,
      loginCountry: this.$session.get('country')
    };
  },
  watch: {
    selectedReasonArray: function() {
      // Ensure only one reason is selected (like existing blackTypeModal pattern)
      if (this.selectedReasonArray.length > 1) {
        this.selectedReasonArray.splice(0, 1);
      }
    }
  },
  async mounted() {
    await this.loadBanReasons();
  },
  methods: {
    buildHeaders() {
      return { Authorization: this.$session.get("token") };
    },
    buildCountryParams() {
      const country = this.$session.get("country");
      if (typeof country === "string" && country.length > 0) {
        return { country };
      }
      return {};
    },
    async loadBanReasons() {
      try {
        const response = await reports.getBanReasons(this.buildHeaders());
        
        // Format response for checkbox group (adjust based on actual API response structure)
        // 폐기 사유는 새로 고를 수 없게 거른다. 이 API는 description_ko 원본을 그대로 내려주므로
        // 라벨을 비워도 안 가려진다 — 거르는 수단이 이 필터뿐이다.
        this.banReasons = response.data
          .filter(reason => reason.deprecated === false)
          .map(reason => ({
            value: reason.id,
            text: this.getLocalizedText(reason)
          }));
      } catch (error) {
        console.error("Failed to load ban reasons:", error);
        this.$toast.error("정지 사유를 불러오는데 실패했습니다.", {
          position: "top",
          duration: 2000,
        });
      }
    },
    getLocalizedText(reason) {
      // Always use Korean description for all users
      const baseText =
        reason.description_ko || reason.description || reason.name || `Reason ${reason.id}`;
      // 통화 HATE_TALK과 피드 FEED_HATE는 한국어 라벨이 글자까지 같아 딱지 없이는 구분할 수 없다
      const domainLabel = getDomainLabel(reason.key);
      return domainLabel ? `[${domainLabel}] ${baseText}` : baseText;
    },
    showReasonModal() {
      this.reasonModalShow = true;
    },
    proceedToConfirmation() {
      if (this.selectedReasonArray.length === 0) return;
      
      // Find the selected reason object
      const selectedReasonId = this.selectedReasonArray[0];
      this.selectedReason = this.banReasons.find(reason => reason.value === selectedReasonId);
      
      // Close reason modal and show confirmation modal
      this.reasonModalShow = false;
      this.confirmModalShow = true;
    },
    cancelReasonSelection() {
      this.reasonModalShow = false;
      this.selectedReasonArray = [];
      this.selectedReason = null;
    },
    cancelBan() {
      this.confirmModalShow = false;
      this.selectedReasonArray = [];
      this.selectedReason = null;
    },
    async executeBan() {
      if (!this.selectedReason) return;
      
      this.isExecuting = true;
      try {
        const response = await reports.banUser(
          this.userId,
          ["CALL", "FEED", "SWIPE"],
          this.selectedReason.value,
          this.buildHeaders()
        );
        
        this.confirmModalShow = false;
        this.selectedReasonArray = [];
        this.selectedReason = null;
        
        this.$toast.success(
          `유저가 성공적으로 영구정지되었습니다.`,
          {
            position: "top",
            duration: 2000,
          }
        );

        // Emit success event to parent component
        this.$emit('ban-success', {
          userId: this.userId,
          response: response,
          reason: this.selectedReason
        });
        
      } catch (error) {
        console.error("Failed to ban user:", error);
        this.$toast.error("영구정지 처리 중 오류가 발생했습니다.", {
          position: "top",
          duration: 2000,
        });
        
        // Emit error event to parent component
        this.$emit('ban-error', {
          userId: this.userId,
          error: error
        });
      } finally {
        this.isExecuting = false;
      }
    }
  }
};
</script>

<style scoped>
.resultBtn {
  margin: 5px 10px 0px 10px;
  word-break: keep-all;
  word-wrap: normal;
}
</style>
